import * as _ from 'lodash'
import { v4 as uuid } from 'uuid';
import { addMonths, addYears, differenceInMonths, formatRelative, format } from 'date-fns';
import valueFilters from './valueFilters';
import { PhoneNumberFormat, PhoneNumberUtil, AsYouTypeFormatter } from 'google-libphonenumber';
import globalRelay from './globalRelay';
const ValidationResult = PhoneNumberUtil.ValidationResult;
const phoneUtil = PhoneNumberUtil.getInstance();

const phoneHelper = {
	testPossible (number, countryCode?) {
		return phoneHelper._getValidationResultKey(phoneHelper._testPossible(number, countryCode));
	},

	assertPossible (number, countryCode?) {
		const result = phoneHelper._testPossible(number, countryCode);

		if (result !== ValidationResult.IS_POSSIBLE) {
			throw new Error('Invalid phone number: ' + phoneHelper._getValidationResultKey(result));
		}
		return true;
	},

	_testPossible (number, countryCode?) {
		countryCode = countryCode || 'CA';
		return phoneUtil.isPossibleNumberWithReason(phoneUtil.parse(number, countryCode));
	},

	formatE164 (number, countryCode?) {
		countryCode = countryCode || 'CA';
		const phoneNumber = phoneUtil.parse(number, countryCode);

		return phoneUtil.format(phoneNumber, PhoneNumberFormat.E164);
	},

	formatNational (number, countryCode?) {
		countryCode = countryCode || 'CA';
		const phoneNumber = phoneUtil.parse(number, countryCode);

		return phoneUtil.format(phoneNumber, PhoneNumberFormat.NATIONAL);
	},

	formatInternational (number, countryCode?) {
		countryCode = countryCode || 'CA';
		const phoneNumber = phoneUtil.parse(number, countryCode);

		return phoneUtil.format(phoneNumber, PhoneNumberFormat.INTERNATIONAL);
	},

	formatUrl (number, countryCode?) {
		countryCode = countryCode || 'CA';
		const phoneNumber = phoneUtil.parse(number, countryCode);

		return phoneUtil.format(phoneNumber, PhoneNumberFormat.RFC3966);
	},

	tryFormat (val, countryCode?) {
		countryCode = countryCode || 'CA';
		const clean = valueFilters.clean(val);
		let result = '';
		const formatter = new AsYouTypeFormatter(countryCode);

		if (clean) {
			for (let i = 0, l = clean.length; i < l; i++) {
				result = formatter.inputDigit(clean[i]);
			}
		}

		return result;
	},

	_getValidationResultKey (val) {
		let result = '';

		Object.keys(ValidationResult).some(function (x) {
			if (ValidationResult[x] === val) {
				result = x;
				return true;
			}
			return false;
		});
		return result;
	}
};

const dateHelper = {
	formatDate (date, options: { userFriendly?: boolean, format?: string }) {
		options = options || {};

		if(!date) {
			return '';
		}

		if (options.userFriendly) {
			return formatRelative(date, new Date(), { locale: globalRelay.dateLocale });
		}
		else if (options.format) {
			return format(date, options.format, { locale: globalRelay.dateLocale });
		}
		else {
			return format(date, "MM/dd/yyyy 'at' HH:mm:ss", { locale: globalRelay.dateLocale });
		}
	},
	addMonths (date, months) {
		return addMonths(date, months);
	},
	addYears (date, years) {
		return addYears(date, years);
	},
	diffMonths (date1, date2) {
		return differenceInMonths(date1, date2);
	}
};

const listHelper = {
	isEmptyListIterable (cursorList) {
		let hasValues;
		cursorList.forEach(it => hasValues = it);
		return hasValues;
	}
}

const costCalculator = {
	getSellWithCostAndMargin (cost, margin) {
		return Math.abs(margin) === 1 ? 0 : (cost / (1 - margin));
	},
	getMarginWithCostAndSell (cost, sell) {
		return Math.abs(sell) !== 0 ? ((sell - cost) / sell) : 0;
	}
};

const STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;
const ARGUMENT_NAMES = /([^\s,]+)/g;
const util = {
	generateGuid () {
		return uuid();
	},

	decodeAuthToken (token) {
		const segments = token.split('.');
		const decoded = globalThis.atob(segments[1]); // the second segment is the content which is our only concern
		const json = JSON.parse(decoded);

		return {
			key: json.sub,
			userName: json.unique_name,
			claims: (typeof json.relay_modules === 'string') ? [json.relay_modules] : json.relay_modules,
			primaryModule: json.relay_primary_module,
			expires: new Date((json.exp || 0) * 1000)
		};
	},

	getParamNames (func) {
		const fs = func.toString().replace(STRIP_COMMENTS, '');
		const argNames = fs.slice(fs.indexOf('(') + 1, fs.indexOf(')')).match(ARGUMENT_NAMES);

		return argNames || [];
	},

	reactVal (rawValue) {
		return (typeof rawValue === 'undefined' || rawValue === null) ? '' : rawValue;
	},

	formatRelayNumber (modelName, relayId) {
		let prefix;

		switch (modelName) {
		case 'Job':
			prefix = 'J-';
			break;
		case 'JobCost':
			prefix = 'JC-';
			break;
		case 'ChangeOrder':
			prefix = 'CO-';
			break;
		case 'JobPayment':
			prefix = 'P-';
			break;
		default:
			prefix = '';
			break;
		}

		return `${prefix}${relayId}`;
	},

	parseRelayNumber (relayNumber) {
		const parts = relayNumber.split('-');

		if (parts.length === 1) {
			return { relayId: Number(parts[0]) };
		}
		else {
			const prefix = parts[0];
			let modelName;

			switch (prefix) {
			case 'J':
				modelName = 'Job';
				break;
			case 'JC':
				modelName = 'JobCost';
				break;
			case 'CO':
				modelName = 'ChangeOrder';
				break;
			case 'P':
				modelName = 'JobPayment';
				break;
			default:
				break;
			}

			return { relayId: Number(parts[0]), modelName: modelName };
		}
	},

	convertToDecimal: function (val) {
		const valString = val ? val.toString() : '';
		const fractionMatch = /(\d*\s+)?(\d+)\/(\d+)/.exec(val);

		if (fractionMatch) {
			return Number(fractionMatch[1]) + (Number(fractionMatch[2]) / Number(fractionMatch[3]));
		}
		else {
			return Number(valueFilters.cleanNumber(valString));
		}
	},

	formatAsFraction: function (val, denoms?: number[]) {
		const cleanVal = valueFilters.cleanNumber(val);
		let valString = cleanVal ? cleanVal.toString() : '';

		if (valString === '') {
			return valString;
		}
		else {
			const parts = valString.split('.');
			denoms = denoms || [2, 4, 8, 16, 32];

			if (parts.length === 2) {
				_.forEach(denoms, d => {
					const fraction = 1 / d;
					const fractional = Number('.' + parts[1]);
					const numerator = fractional / fraction;
					if (numerator === Math.floor(numerator)) {
						valString = parts[0] + ' ' + numerator.toString() + '/' + d.toString();
						return false;
					}
				});
			}

			return valString;
		}
	},

	formatAsCurrency: function (val) {
		let numVal = Number(val);

		numVal = isNaN(numVal) ? 0 : numVal;

		const numString = numVal.toFixed(2);
		const numParts = numString.split('.');
		const wholeNumChars = numParts[0].split('');
		if (numVal < 0) {
			wholeNumChars.splice(0, 1);
		}
		const resultChars:string[] = [];
		const len = wholeNumChars.length;
		let i = 1;

		for (i; i <= len; i++) {
			if (i !== 1 && (i - 1) % 3 === 0) {
				resultChars.unshift(',');
			}
			resultChars.unshift(wholeNumChars[len - i]);
		}

		return `${numVal < 0 ? '-' : ''}$${resultChars.join('')}.${numParts[1]}`;
	},

	formatScaled: function (val) {
		let numVal = Number(val);
		let roundedVal;
		let unit = '';

		numVal = isNaN(numVal) ? 0 : numVal;

		if (numVal < 1000) {
			unit = '';
			roundedVal = Math.round(numVal) * 10;
		}
		else if (numVal < 1000000) {
			unit = 'K';
			roundedVal = Math.round(numVal / 100);
		}
		else if (numVal < 1000000000) {
			unit = 'M';
			roundedVal = Math.round(numVal / 100000);
		}
		else if (numVal < 1000000000000) {
			unit = 'B';
			roundedVal = Math.round(numVal / 100000000);
		}

		roundedVal /= 10;

		if (Math.floor(roundedVal) === roundedVal) {
			return `${roundedVal.toFixed(0)} ${unit}`;
		}
		else {
			return `${roundedVal.toFixed(1)} ${unit}`;
		}
	},

	roundAsCurrency (val) {
		const originalVal = Number(val || 0);

		return Number(originalVal.toFixed(2));
	},

	mergePatch: function (targetPatch, patch) {
		if (patch && patch !== targetPatch) {
			// merge the new patch with the target patch
			_.forEach(patch, op => {
				const idx = _.findIndex(targetPatch, { path: op.path });
				if (idx === -1) {
					targetPatch.push(op);
				}
				else {
					const existing = targetPatch[idx];
					if (existing.op === 'add') {
						if (op.op === 'remove') {
							targetPatch.splice(idx, 1);
						}
						else {
							existing.value = op.value;
						}
					}
					else {
						targetPatch.splice(idx, 1);
						targetPatch.push(op);
					}
				}
			});
		}

		return targetPatch;
	},

	// from: https://stackoverflow.com/questions/12168909/blob-from-dataurl
	dataURItoBlob: function (dataURI) {
		// convert base64 to raw binary data held in a string
		// doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
		const byteString = atob(dataURI.split(',')[1]);

		// separate out the mime component
		const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

		// write the bytes of the string to an ArrayBuffer
		const ab = new ArrayBuffer(byteString.length);

		// create a view into the buffer
		const ia = new Uint8Array(ab);

		// set the bytes of the buffer to the correct values
		for (let i = 0; i < byteString.length; i++) {
			ia[i] = byteString.charCodeAt(i);
		}

		// write the ArrayBuffer to a blob, and you're done
		const blob = new Blob([ab], { type: mimeString });

		return blob;
	},

	phoneHelper,
	dateHelper,
	costCalculator,
	listHelper
};

export default util;